/**
 * Diffie-Hellman and signing over P-256 (WebCrypto) for the
 * `realtime-e2e-messaging` E2E messaging protocol.
 *
 * - **ECDH P-256** is the DH primitive of the X3DH handshake and the double ratchet.
 *   It relies on the same curves as the `e2e-crypto` kit (Vault) so as not to
 *   introduce new, unaudited cryptography.
 * - **ECDSA P-256** signs the prekeys and the group messages (sender-keys):
 *   WebCrypto does not let one key do both ECDH and ECDSA, so an
 *   identity publishes TWO public keys (one DH, one signing).
 *
 * All material travels as raw bytes (`raw` for public, `pkcs8` for
 * private) so it can be serialized, cached and compared without coupling to
 * non-exportable `CryptoKey` objects.
 */

const CURVE = "P-256";

export interface RawKeyPair {
  /** Public key in raw format (65 bytes). NOT secret. */
  readonly publicKey: Uint8Array;
  /** Private key in PKCS#8. SECRET. */
  readonly privateKey: Uint8Array;
}

function ab(bytes: Uint8Array): ArrayBuffer {
  const copy = new ArrayBuffer(bytes.length);
  new Uint8Array(copy).set(bytes);
  return copy;
}

// ---------------------------------------------------------------------------
// ECDH (key agreement)
// ---------------------------------------------------------------------------

export async function generateDhKeyPair(): Promise<RawKeyPair> {
  const pair = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: CURVE }, true, [
    "deriveBits",
  ]);
  const publicKey = new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey));
  const privateKey = new Uint8Array(await crypto.subtle.exportKey("pkcs8", pair.privateKey));
  return { publicKey, privateKey };
}

async function importDhPublic(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", ab(raw), { name: "ECDH", namedCurve: CURVE }, true, []);
}

async function importDhPrivate(pkcs8: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("pkcs8", ab(pkcs8), { name: "ECDH", namedCurve: CURVE }, false, [
    "deriveBits",
  ]);
}

/** Raw output of the ECDH agreement (32 bytes, X coordinate of the shared point). */
export async function dh(privateKey: Uint8Array, publicKey: Uint8Array): Promise<Uint8Array> {
  const priv = await importDhPrivate(privateKey);
  const pub = await importDhPublic(publicKey);
  const bits = await crypto.subtle.deriveBits({ name: "ECDH", public: pub }, priv, 256);
  return new Uint8Array(bits);
}

// ---------------------------------------------------------------------------
// ECDSA (signing)
// ---------------------------------------------------------------------------

export async function generateSigningKeyPair(): Promise<RawKeyPair> {
  const pair = await crypto.subtle.generateKey({ name: "ECDSA", namedCurve: CURVE }, true, [
    "sign",
    "verify",
  ]);
  const publicKey = new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey));
  const privateKey = new Uint8Array(await crypto.subtle.exportKey("pkcs8", pair.privateKey));
  return { publicKey, privateKey };
}

async function importSignPrivate(pkcs8: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("pkcs8", ab(pkcs8), { name: "ECDSA", namedCurve: CURVE }, false, [
    "sign",
  ]);
}

async function importSignPublic(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", ab(raw), { name: "ECDSA", namedCurve: CURVE }, true, [
    "verify",
  ]);
}

export async function sign(privateKey: Uint8Array, message: Uint8Array): Promise<Uint8Array> {
  const key = await importSignPrivate(privateKey);
  const sig = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, key, ab(message));
  return new Uint8Array(sig);
}

export async function verify(
  publicKey: Uint8Array,
  message: Uint8Array,
  signature: Uint8Array,
): Promise<boolean> {
  const key = await importSignPublic(publicKey);
  return crypto.subtle.verify({ name: "ECDSA", hash: "SHA-256" }, key, ab(signature), ab(message));
}
